// Testing session recorder.
//
// Activates only when the URL has `?aclymate-testing-session={sessionId}` +
// `?recorder-token={token}` query params (typically opened by the
// aclymate-testing portal). On every subsequent page in this tab, the recorder
// streams events to the internal Cloud Functions backend so an AI-driven fix
// loop has rich context about what the tester was actually doing.
//
// Strictly no-op when not in a testing session.

const SESSION_KEY = "aclymate-testing-session";
const TOKEN_KEY = "aclymate-testing-recorder-token";
const FUNCTIONS_BASE_URL =
  "https://us-central1-aclymate-internal.cloudfunctions.net";
const FLUSH_INTERVAL_MS = 4000;
const MAX_BUFFER = 25;
const MAX_FIELD_LEN = 800;

const truncate = (value) => {
  if (value == null) return null;
  const s = String(value);
  return s.length > MAX_FIELD_LEN ? `${s.slice(0, MAX_FIELD_LEN)}…[truncated]` : s;
};

const describeElement = (el) => {
  if (!el || el.nodeType !== 1) return null;
  return {
    tag: el.tagName?.toLowerCase() || null,
    id: el.id || null,
    classes:
      el.className && typeof el.className === "string"
        ? el.className.slice(0, 200)
        : null,
    text: truncate(el.textContent?.trim() || null),
    role: el.getAttribute("role"),
    "aria-label": el.getAttribute("aria-label"),
    name: el.getAttribute("name"),
    href: el.getAttribute("href"),
  };
};

const MEANINGFUL_SELECTOR =
  'button, a, [role="button"], [role="link"], [role="menuitem"], [role="tab"], input, label';

const findMeaningfulAncestor = (node, depth = 0) => {
  if (!node || depth >= 5) return null;
  if (node.matches && node.matches(MEANINGFUL_SELECTOR)) return node;
  return findMeaningfulAncestor(node.parentElement, depth + 1);
};

const readSessionFromUrlOrStorage = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get(SESSION_KEY);
    const tokenFromUrl = params.get("recorder-token");
    if (fromUrl && tokenFromUrl) {
      sessionStorage.setItem(SESSION_KEY, fromUrl);
      sessionStorage.setItem(TOKEN_KEY, tokenFromUrl);
      params.delete(SESSION_KEY);
      params.delete("recorder-token");
      const clean = params.toString()
        ? `${window.location.pathname}?${params.toString()}${window.location.hash}`
        : `${window.location.pathname}${window.location.hash}`;
      window.history.replaceState({}, document.title, clean);
      return { sessionId: fromUrl, token: tokenFromUrl };
    }
    const fromStorage = sessionStorage.getItem(SESSION_KEY);
    const tokenFromStorage = sessionStorage.getItem(TOKEN_KEY);
    if (fromStorage && tokenFromStorage) {
      return { sessionId: fromStorage, token: tokenFromStorage };
    }
  } catch {
    // sessionStorage can throw in private-mode browsers — bail silently.
  }
  return null;
};

const createRecorder = ({ sessionId, token }) => {
  const state = {
    buffer: [],
    flushing: false,
    flushTimer: null,
    lastUrl: window.location.href,
  };

  const flush = async () => {
    if (state.flushTimer) {
      clearTimeout(state.flushTimer);
      state.flushTimer = null;
    }
    if (state.flushing || state.buffer.length === 0) return;
    const batch = state.buffer.splice(0, state.buffer.length);
    state.flushing = true;
    const payload = JSON.stringify({
      sessionId,
      recorderToken: token,
      events: batch,
    });
    try {
      const url = `${FUNCTIONS_BASE_URL}/recordTestingEvent`;
      // text/plain content type keeps this a "simple" CORS request — no
      // preflight, no credentials-mode-include problems. The function parses
      // the body as JSON server-side.
      const blob = new Blob([payload], { type: "text/plain" });
      const beaconOk =
        navigator.sendBeacon && navigator.sendBeacon(url, blob);
      if (!beaconOk) {
        await fetch(url, {
          method: "POST",
          keepalive: true,
          mode: "cors",
          credentials: "omit",
          headers: { "Content-Type": "text/plain" },
          body: payload,
        });
      }
    } catch {
      // Drop on the floor — never disrupt the host app.
    } finally {
      state.flushing = false;
    }
  };

  const push = (event) => {
    state.buffer.push({
      ts: new Date().toISOString(),
      url: window.location.href,
      ...event,
    });
    if (state.buffer.length >= MAX_BUFFER) {
      flush();
      return;
    }
    if (!state.flushTimer) {
      state.flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
    }
  };

  const emitIfChanged = () => {
    if (window.location.href !== state.lastUrl) {
      state.lastUrl = window.location.href;
      push({ type: "navigation" });
    }
  };

  const wrapHistoryMethod = (key) => {
    const original = window.history[key];
    window.history[key] = function wrapped(...args) {
      const r = original.apply(this, args);
      setTimeout(emitIfChanged, 0);
      return r;
    };
  };

  const watchRouteChanges = () => {
    wrapHistoryMethod("pushState");
    wrapHistoryMethod("replaceState");
    window.addEventListener("popstate", emitIfChanged);
    window.addEventListener("hashchange", emitIfChanged);
  };

  const watchConsole = () => {
    const orig = console.error.bind(console);
    console.error = (...args) => {
      try {
        push({
          type: "console-error",
          message: truncate(
            args
              .map((a) => (a instanceof Error ? a.stack || a.message : String(a)))
              .join(" ")
          ),
        });
      } catch {
        // ignore — keep host app running
      }
      orig(...args);
    };
  };

  const watchUnhandled = () => {
    window.addEventListener("error", (event) => {
      push({
        type: "window-error",
        message: truncate(event.message),
        source: event.filename,
        line: event.lineno,
        col: event.colno,
        stack: truncate(event.error?.stack),
      });
    });
    window.addEventListener("unhandledrejection", (event) => {
      push({
        type: "unhandled-rejection",
        message: truncate(event.reason?.message || String(event.reason)),
        stack: truncate(event.reason?.stack),
      });
    });
  };

  const watchNetwork = () => {
    if (typeof window.fetch !== "function") return;
    const origFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const method = (
        init?.method ||
        (typeof input === "object" && input?.method) ||
        "GET"
      ).toUpperCase();
      const url = typeof input === "string" ? input : input?.url || "";
      const startedAt = Date.now();
      try {
        const response = await origFetch(input, init);
        if (response.status >= 400) {
          push({
            type: "network-failure",
            method,
            url: truncate(url),
            status: response.status,
            durationMs: Date.now() - startedAt,
          });
        }
        return response;
      } catch (err) {
        push({
          type: "network-error",
          method,
          url: truncate(url),
          message: truncate(err?.message),
          durationMs: Date.now() - startedAt,
        });
        throw err;
      }
    };
  };

  const watchClicks = () => {
    document.addEventListener(
      "click",
      (event) => {
        const meaningful =
          findMeaningfulAncestor(event.target) || event.target;
        push({ type: "click", target: describeElement(meaningful) });
      },
      true
    );
  };

  const watchInputs = () => {
    document.addEventListener(
      "change",
      (event) => {
        const el = event.target;
        if (!el || !el.tagName) return;
        if (el.tagName === "INPUT" && el.type === "password") return;
        push({
          type: "input-change",
          target: describeElement(el),
          value: truncate(el.value),
        });
      },
      true
    );
  };

  const watchUnload = () => {
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
  };

  const start = () => {
    push({ type: "session-start", userAgent: navigator.userAgent });
    watchRouteChanges();
    watchConsole();
    watchUnhandled();
    watchNetwork();
    watchClicks();
    watchInputs();
    watchUnload();
  };

  const captureDomSnapshot = () => {
    push({
      type: "dom-snapshot",
      title: document.title,
      bodyOuterHtml: truncate(document.body?.outerHTML?.slice(0, 50_000)),
    });
    flush();
  };

  return { start, flush, push, captureDomSnapshot };
};

const recorderState = { started: false, instance: null };

export const startTestingRecorder = () => {
  if (recorderState.started || typeof window === "undefined") return;
  const session = readSessionFromUrlOrStorage();
  if (!session) return;
  recorderState.started = true;
  try {
    const recorder = createRecorder(session);
    recorder.start();
    recorderState.instance = recorder;
    window.__aclymateTestingRecorder = recorder;
    // eslint-disable-next-line no-console
    console.info(
      "[aclymate-testing-recorder] active for session",
      `${session.sessionId.slice(0, 8)}…`
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[aclymate-testing-recorder] failed to start:", err);
  }
};

export const captureDomSnapshot = () => {
  if (recorderState.instance) recorderState.instance.captureDomSnapshot();
};
